/**
 * Shared config for the animated boot-splash mosaic.
 *
 * The Ken-Burns lab (/boot-lab-v2) lets the user tap a heart on any
 * variant they like; that list gets stored in localStorage and
 * <BootSplash /> pulls a RANDOM entry from it on every session start.
 *
 * If the user hasn't picked anything yet we fall back to all 20 IDs so
 * every session spins a genuinely different splash.
 *
 * Feb-2026 (user "infinite mosaic" pass): NONE of the animations
 * shrink the mosaic below scale(1.0) at the end. Every keyframe now
 * ends at scale ≥ 1.0 with opacity → 0, so as the logo grows in the
 * user only sees the photos FADE (not funnel into a tiny centred
 * square that exposes the grid's finite edge).
 */

const EXCLUDED_SPEAKERS = new Set([
  "R' Yisroel Becker",
  "R' ruvain-becker-e1719512823531-230x300",
]);

/* ─────────────────────────────────────────────
 * 20 variants — kept in sync with BootLabV2Page.
 * Each `id` is the tile number (#01..#20) so users
 * can reference them casually in chat.
 * ───────────────────────────────────────────── */
export const BOOT_VARIANTS = [
  { id: 1,  cols: 8,  seed: 101, anim: 'kb-slow',         logo: 'logo-pop',   dim: 'dim-slow',    gap: 2 },
  { id: 2,  cols: 12, seed: 102, anim: 'kb-drift-diag',   logo: 'logo-fade',  dim: 'dim-slow',    gap: 2 },
  { id: 3,  cols: 14, seed: 103, anim: 'kb-zoom-in',      logo: 'logo-pop',   dim: 'dim-fast',    gap: 2 },
  { id: 4,  cols: 16, seed: 104, anim: 'kb-slow',         logo: 'logo-swing', dim: 'dim-slow',    gap: 1 },
  { id: 5,  cols: 20, seed: 105, anim: 'kb-still-pan',    logo: 'logo-drop',  dim: 'dim-slow',    gap: 1 },
  { id: 6,  cols: 12, seed: 106, anim: 'kb-zoom-out',     logo: 'logo-fade',  dim: 'dim-slow',    gap: 2 },
  { id: 7,  cols: 14, seed: 107, anim: 'kb-hold-implode', logo: 'logo-pop',   dim: 'dim-fast',    gap: 2 },
  { id: 8,  cols: 10, seed: 108, anim: 'kb-pan-lr',       logo: 'logo-pop',   dim: 'dim-slow',    gap: 2 },
  { id: 9,  cols: 16, seed: 109, anim: 'kb-tilt-diag',    logo: 'logo-pop',   dim: 'dim-slow',    gap: 1 },
  { id: 10, cols: 18, seed: 110, anim: 'kb-radial',       logo: 'logo-fade',  dim: 'dim-slow',    gap: 1 },
  { id: 11, cols: 12, seed: 111, anim: 'kb-slow',         logo: 'logo-pop',   dim: 'dim-gold',    gap: 2 },
  { id: 12, cols: 14, seed: 112, anim: 'kb-breathe',      logo: 'logo-pop',   dim: 'dim-slow',    gap: 2 },
  { id: 13, cols: 10, seed: 113, anim: 'kb-face-first',   logo: 'logo-pop',   dim: 'dim-slow',    gap: 3 },
  { id: 14, cols: 16, seed: 114, anim: 'kb-pan-tb',       logo: 'logo-swing', dim: 'dim-slow',    gap: 1 },
  { id: 15, cols: 20, seed: 115, anim: 'kb-minimal',      logo: 'logo-fade',  dim: 'dim-blur',    gap: 1 },
  { id: 16, cols: 14, seed: 116, anim: 'kb-circle',       logo: 'logo-pop',   dim: 'dim-slow',    gap: 2 },
  { id: 17, cols: 12, seed: 117, anim: 'kb-portal',       logo: 'logo-fade',  dim: 'dim-fast',    gap: 2 },
  { id: 18, cols: 16, seed: 118, anim: 'kb-minimal',      logo: 'logo-pulse', dim: 'dim-vignette',gap: 1 },
  { id: 19, cols: 8,  seed: 119, anim: 'kb-cinematic',    logo: 'logo-swing', dim: 'dim-slow',    gap: 2 },
  { id: 20, cols: 16, seed: 120, anim: 'kb-funnel',       logo: 'logo-pop',   dim: 'dim-slow',    gap: 1 },
];

export const BOOT_VARIANT_LABELS = {
  1:  'Big Squares · Slow Zoom',
  2:  'Dense 12 · Diagonal Pan',
  3:  'Dense 14 · Heavy Zoom-In',
  4:  'Dense 16 · Micro Squares',
  5:  'Ultra Dense 20 · Still Pan',
  6:  'Dense 12 · Zoom-Out From Face',
  7:  'Dense 14 · Static + End Contract',
  8:  'Dense 10 · Left-to-Right Pan',
  9:  'Dense 16 · Diagonal + Tilt',
  10: 'Dense 18 · Radial Zoom',
  11: 'Dense 12 · Golden Vignette',
  12: 'Dense 14 · Breathe · Contract',
  13: 'Big 10-col · Face-First',
  14: 'Dense 16 · Vertical Sweep',
  15: 'Ultra 20 · Speckled + Fade',
  16: 'Dense 14 · Circular Pan',
  17: 'Dense 12 · Zoom-Through Portal',
  18: 'Dense 16 · Slow Drift + Vignette',
  19: 'Big 8-col · Cinematic KB',
  20: 'Dense 16 · Contract-to-Dot',
};

export const BOOT_VARIANT_BLURBS = {
  1:  '8×12 tiles, calm Ken-Burns, logo pops in',
  2:  '12×16 tiles, drifting down-right, logo fades up',
  3:  '14×20, zooms hard into center, contracts to logo',
  4:  '16×22, slow Ken-Burns, logo swings in',
  5:  '20×28 xs tiles, subtle horizontal pan, logo drops',
  6:  'Starts zoomed in on random face, pulls back, logo emerges',
  7:  'Grid holds still, then implodes to logo at end',
  8:  'Wide 10×14, camera pans across, logo pops',
  9:  'Almost imperceptible tilt, dense pan, contract to logo',
  10: 'Very dense grid, camera zooms outward, logo materialises',
  11: 'Warm gold vignette wash over slow Ken-Burns',
  12: 'Grid starts small, expands out, then contracts back to logo',
  13: 'Larger 10×14 squares, faces read clearly, gentle drift',
  14: '16×22 pans top-to-bottom, logo unfolds',
  15: 'Ultra dense, minimal motion, logo fades in over blur',
  16: 'Camera moves in a subtle circle, logo pops at center',
  17: 'Whole grid flies past camera, logo emerges from center',
  18: 'Barely moving 16×22 with dark vignette, logo pulses in',
  19: 'Big 8×12 tiles, dramatic zoom + pan, logo swings in',
  20: '16×22 pan then EVERY tile funnels into the logo',
};

/* ─────────────────────────────────────────────
 * localStorage helpers — persistent across
 * sessions AND devices bound to that browser.
 * ───────────────────────────────────────────── */
const LS_KEY = 'vaad_boot_variants';

// User wants ALL 20 variants active by default so each new session
// spins a completely different splash. Heart-toggle logic in the lab
// still lets a future user narrow it down, but shipping-default = full
// random rotation.
export const DEFAULT_ENABLED_VARIANTS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
  11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];

/** Read the set of picked variant IDs from localStorage. Returns an
 *  array of integer IDs. Falls back to DEFAULT_ENABLED_VARIANTS when
 *  the key is missing or invalid. */
export function getEnabledVariantIds() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_ENABLED_VARIANTS.slice();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const valid = parsed.filter((n) => Number.isInteger(n) && n >= 1 && n <= 20);
      if (valid.length > 0) return valid;
    }
  } catch { /* ignore parse errors */ }
  return DEFAULT_ENABLED_VARIANTS.slice();
}

/** Persist the picks — call with an array of variant IDs. */
export function setEnabledVariantIds(ids) {
  try {
    const clean = Array.from(new Set(ids.filter((n) => Number.isInteger(n) && n >= 1 && n <= 20)));
    localStorage.setItem(LS_KEY, JSON.stringify(clean));
    // Notify any lab tab open so it can re-render heart states in sync.
    window.dispatchEvent(new CustomEvent('vaad-boot-variants-changed', { detail: clean }));
  } catch { /* localStorage denied — ignore, splash will fall back */ }
}

/** Toggle a single ID on/off. Returns the new list. */
export function toggleEnabledVariant(id) {
  const cur = new Set(getEnabledVariantIds());
  if (cur.has(id)) cur.delete(id);
  else cur.add(id);
  const next = Array.from(cur);
  setEnabledVariantIds(next);
  return next;
}

/** Randomly pick ONE variant from the user's saved picks. If they've
 *  picked nothing, uses DEFAULT_ENABLED_VARIANTS. */
export function pickRandomVariant() {
  const ids = getEnabledVariantIds();
  const chosen = ids[Math.floor(Math.random() * ids.length)];
  return BOOT_VARIANTS.find((v) => v.id === chosen) || BOOT_VARIANTS[1]; // #02 fallback
}

/* ─────────────────────────────────────────────
 * Excluded-speaker filter — kept here so both
 * the lab and the splash apply the same rule.
 * ───────────────────────────────────────────── */
export function poolWithExcludes(pool) {
  const out = [];
  for (const [speaker, ids] of Object.entries(pool || {})) {
    if (EXCLUDED_SPEAKERS.has(speaker)) continue;
    for (const fid of ids) out.push({ fid, speaker });
  }
  return out;
}

/* ─────────────────────────────────────────────
 * Full CSS keyframe library. Import as a string
 * and drop it inside a <style> tag once per
 * component instance.
 *
 * BOOT_DURATION_MS controls both mosaic + logo
 * timelines; change here to speed up/slow down
 * the whole splash without touching keyframes.
 * ───────────────────────────────────────────── */
export const BOOT_DURATION_MS = 3200;

export const BOOT_KEYFRAMES = `
:root { --boot-dur: ${BOOT_DURATION_MS}ms; }

/* ─── Ken-Burns tracks ───
 * All tracks end at scale ≥ 1.0 with opacity → 0. The mosaic never
 * shrinks below viewport size, so the finite edge of the grid never
 * shows through — users see the photos FADE while the logo grows,
 * giving that "portraits go on forever" feel. */

@keyframes kbSlow {
  0%   { transform: scale(1.12) translate(-2%, -1%); opacity: 1; }
  60%  { transform: scale(1.22) translate(2%, 2%);   opacity: 1; }
  100% { transform: scale(1.30) translate(3%, 3%);   opacity: 0; }
}
.kb-slow { animation: kbSlow var(--boot-dur) cubic-bezier(.5,.05,.3,1) forwards; }

@keyframes kbDriftDiag {
  0%   { transform: scale(1.25) translate(-4%, -3%); opacity: 1; }
  55%  { transform: scale(1.35) translate(4%, 3%);   opacity: 1; }
  100% { transform: scale(1.45) translate(6%, 5%);   opacity: 0; }
}
.kb-drift-diag { animation: kbDriftDiag var(--boot-dur) cubic-bezier(.55,.05,.3,1) forwards; }

@keyframes kbZoomIn {
  0%   { transform: scale(1.00); opacity: 1; }
  70%  { transform: scale(1.55); opacity: 1; }
  100% { transform: scale(1.85); opacity: 0; }
}
.kb-zoom-in { animation: kbZoomIn var(--boot-dur) cubic-bezier(.5,.1,.3,1) forwards; }

@keyframes kbStillPan {
  0%   { transform: scale(1.08) translate(-2%, 0); opacity: 1; }
  60%  { transform: scale(1.10) translate(2%, 0);  opacity: 1; }
  100% { transform: scale(1.18) translate(3%, 0);  opacity: 0; }
}
.kb-still-pan { animation: kbStillPan var(--boot-dur) ease-out forwards; }

@keyframes kbZoomOut {
  0%   { transform: scale(2.6) translate(-4%, -3%); opacity: 1; }
  70%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1.05); opacity: 0; }
}
.kb-zoom-out { animation: kbZoomOut var(--boot-dur) cubic-bezier(.4,.1,.3,1) forwards; }

@keyframes kbHoldImplode {
  0%,  70% { transform: scale(1.12); opacity: 1; }
  100%     { transform: scale(1.35); opacity: 0; }
}
.kb-hold-implode { animation: kbHoldImplode var(--boot-dur) cubic-bezier(.7,.02,.4,1) forwards; }

@keyframes kbPanLR {
  0%   { transform: scale(1.25) translate(-5%, 0); opacity: 1; }
  70%  { transform: scale(1.25) translate(5%, 0);  opacity: 1; }
  100% { transform: scale(1.35) translate(7%, 0);  opacity: 0; }
}
.kb-pan-lr { animation: kbPanLR var(--boot-dur) cubic-bezier(.5,.05,.3,1) forwards; }

@keyframes kbTiltDiag {
  0%   { transform: scale(1.18) rotate(-0.6deg) translate(-2%, -2%); opacity: 1; }
  60%  { transform: scale(1.28) rotate(0.6deg) translate(2%, 2%);    opacity: 1; }
  100% { transform: scale(1.38) rotate(0.3deg) translate(3%, 3%);    opacity: 0; }
}
.kb-tilt-diag { animation: kbTiltDiag var(--boot-dur) cubic-bezier(.5,.05,.3,1) forwards; }

@keyframes kbRadial {
  0%   { transform: scale(1.00); opacity: 0.7; }
  20%  { transform: scale(1.20); opacity: 1; }
  70%  { transform: scale(1.35); opacity: 1; }
  100% { transform: scale(1.55); opacity: 0; }
}
.kb-radial { animation: kbRadial var(--boot-dur) cubic-bezier(.4,.05,.3,1) forwards; }

@keyframes kbBreathe {
  0%   { transform: scale(1.05); opacity: 0.9; }
  40%  { transform: scale(1.30); opacity: 1; }
  70%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1.35); opacity: 0; }
}
.kb-breathe { animation: kbBreathe var(--boot-dur) ease-in-out forwards; }

@keyframes kbFaceFirst {
  0%   { transform: scale(1.05) translate(0, -1%); opacity: 1; }
  70%  { transform: scale(1.14) translate(1%, 1%); opacity: 1; }
  100% { transform: scale(1.22) translate(2%, 2%); opacity: 0; }
}
.kb-face-first { animation: kbFaceFirst var(--boot-dur) ease-in-out forwards; }

@keyframes kbPanTB {
  0%   { transform: scale(1.25) translate(0, -5%); opacity: 1; }
  70%  { transform: scale(1.28) translate(0, 5%);  opacity: 1; }
  100% { transform: scale(1.38) translate(0, 7%);  opacity: 0; }
}
.kb-pan-tb { animation: kbPanTB var(--boot-dur) cubic-bezier(.5,.05,.3,1) forwards; }

@keyframes kbMinimal {
  0%   { transform: scale(1.05); opacity: 1; }
  70%  { transform: scale(1.10); opacity: 1; }
  100% { transform: scale(1.20); opacity: 0; }
}
.kb-minimal { animation: kbMinimal var(--boot-dur) ease-out forwards; }

@keyframes kbCircle {
  0%   { transform: scale(1.22) translate(-2%, -2%); opacity: 1; }
  25%  { transform: scale(1.22) translate(2%, -2%);  opacity: 1; }
  50%  { transform: scale(1.22) translate(2%, 2%);   opacity: 1; }
  75%  { transform: scale(1.22) translate(-2%, 2%);  opacity: 1; }
  100% { transform: scale(1.32); opacity: 0; }
}
.kb-circle { animation: kbCircle var(--boot-dur) cubic-bezier(.5,.05,.5,.95) forwards; }

/* Portal / cinematic feel: camera flies FORWARD through the mosaic
 * — scale keeps growing so nothing ever exposes an edge. */
@keyframes kbPortal {
  0%   { transform: scale(1.00); opacity: 0.4; }
  35%  { transform: scale(1.35); opacity: 1; }
  75%  { transform: scale(2.20); opacity: 0.9; }
  100% { transform: scale(4.50); opacity: 0; }
}
.kb-portal { animation: kbPortal var(--boot-dur) cubic-bezier(.55,.05,.3,1) forwards; }

@keyframes kbCinematic {
  0%   { transform: scale(1.30) translate(-4%, -3%); opacity: 1; }
  50%  { transform: scale(1.55) translate(3%, 3%);   opacity: 1; }
  100% { transform: scale(1.85) translate(5%, 4%);   opacity: 0; }
}
.kb-cinematic { animation: kbCinematic var(--boot-dur) cubic-bezier(.5,.03,.3,1) forwards; }

/* Funnel: originally shrank to scale(0.08) revealing a tiny centred
 * square. New behaviour — camera zooms IN sharply then fades, so
 * photos feel like they're rushing past the viewer. */
@keyframes kbFunnel {
  0%   { transform: scale(1.30) translate(0, 0);    opacity: 1; }
  55%  { transform: scale(1.65) translate(1%, -1%); opacity: 1; }
  100% { transform: scale(2.10); opacity: 0; }
}
.kb-funnel { animation: kbFunnel var(--boot-dur) cubic-bezier(.7,.02,.4,1) forwards; }

/* ─── Dim wash layers ─── */
@keyframes dimSlow { 0%,55% { background: rgba(15,23,41,0);} 100% { background: rgba(15,23,41,0.72);} }
.dim-slow { animation: dimSlow var(--boot-dur) ease-out forwards; pointer-events: none; }

@keyframes dimFast { 0%,40% { background: rgba(15,23,41,0);} 100% { background: rgba(15,23,41,0.82);} }
.dim-fast { animation: dimFast var(--boot-dur) ease-out forwards; pointer-events: none; }

@keyframes dimGold {
  0%,50% { background: radial-gradient(circle at 50% 50%, rgba(201,164,55,0) 0%, rgba(15,23,41,0) 60%);}
  100%   { background: radial-gradient(circle at 50% 50%, rgba(201,164,55,0.28) 0%, rgba(15,23,41,0.72) 65%);}
}
.dim-gold { animation: dimGold var(--boot-dur) ease-out forwards; pointer-events: none; }

@keyframes dimBlur {
  0%,55% { backdrop-filter: blur(0px); background: rgba(15,23,41,0);}
  100%   { backdrop-filter: blur(3px); background: rgba(15,23,41,0.55);}
}
.dim-blur { animation: dimBlur var(--boot-dur) ease-out forwards; pointer-events: none; }

@keyframes dimVignette {
  0%,55% { background: radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%);}
  100%   { background: radial-gradient(circle at 50% 50%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.85) 100%);}
}
.dim-vignette { animation: dimVignette var(--boot-dur) ease-out forwards; pointer-events: none; }

/* ─── Logo entrances ─── */
@keyframes logoPop {
  0%, 55%  { opacity: 0; transform: scale(0.12); }
  80%      { opacity: 1; transform: scale(1.06); }
  100%     { opacity: 1; transform: scale(1); }
}
.logo-pop { animation: logoPop var(--boot-dur) cubic-bezier(.34,1.55,.64,1) forwards; }

@keyframes logoFade {
  0%, 60%  { opacity: 0; transform: scale(0.9); }
  100%     { opacity: 1; transform: scale(1); }
}
.logo-fade { animation: logoFade var(--boot-dur) ease-out forwards; }

@keyframes logoSwing {
  0%, 55%  { opacity: 0; transform: rotate(-12deg) scale(0.4); }
  80%      { opacity: 1; transform: rotate(4deg) scale(1.05); }
  100%     { opacity: 1; transform: rotate(0) scale(1); }
}
.logo-swing { animation: logoSwing var(--boot-dur) cubic-bezier(.34,1.4,.64,1) forwards; }

@keyframes logoDrop {
  0%, 55%  { opacity: 0; transform: translateY(-140%) scale(0.6); }
  80%      { opacity: 1; transform: translateY(6%) scale(1.02); }
  100%     { opacity: 1; transform: translateY(0) scale(1); }
}
.logo-drop { animation: logoDrop var(--boot-dur) cubic-bezier(.4,1.3,.5,1) forwards; }

@keyframes logoPulse {
  0%, 55%  { opacity: 0; transform: scale(0.5); filter: drop-shadow(0 0 0 #c9a437); }
  75%      { opacity: 1; transform: scale(1.08); filter: drop-shadow(0 0 24px #c9a437ee); }
  100%     { opacity: 1; transform: scale(1); filter: drop-shadow(0 0 8px #c9a437aa); }
}
.logo-pulse { animation: logoPulse var(--boot-dur) ease-out forwards; }
`;
